import { Flex } from 'components/shared/flex'
import { usePostHomeProductApi, usePutHomeProductApi } from 'api/home_product'
import { CheckboxField, InputField, MultipleSelectField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { HomeProduct, HomeProductForm } from 'entities/home_product'
import { useFetchProductItemsApi } from 'api/product_item'

export type FormViewProps = {
    form: Form<HomeProductForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    const productItemsApi = useFetchProductItemsApi()

    useEffect(() => {
        productItemsApi.execute()
    }, [])

    return (
        <>
            <Flex>
                <InputField form={form} attr="code" label="商品コード" required />
            </Flex>
            <InputField form={form} attr="name" label="商品名" required />
            <InputField type="color" form={form} attr="color" label="色" required />
            <InputField form={form} attr="price" label="価格(税抜)" required type="number" />
            <MultipleSelectField
                horizontal
                id="select_product_items"
                form={form}
                attr="productItemIds"
                label="対象製品(複数選択可)"
                selectItems={productItemsApi.response.results?.map((pi) => {
                    return { label: pi.name, value: pi.id! }
                })}
            />
            <CheckboxField form={form} attr="isForeignCountry" label="海外用" checkboxLabel="海外用に販売する" style={{ paddingTop: '8px' }} />
            <CheckboxField
                form={form}
                attr="isDivergenceByGender"
                label="性別で別々のプロダクトを紐付ける場合にチェック"
                checkboxLabel="性別で分ける"
                style={{ paddingTop: '8px' }}
            />
            <SelectField
                form={form}
                attr="target"
                label="この製品が紐づく性別"
                style={{ paddingTop: '8px' }}
                selectItems={[
                    { label: '男性', value: 'MALE' },
                    { label: '女性', value: 'FEMALE' },
                ]}
            />
        </>
    )
}

type NewHomeProductFormProps = {
    onComplete?: () => void
    institutionId?: number
}

export const NewHomeProductForm = (props: NewHomeProductFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostHomeProductApi()
    const form = useForm<HomeProductForm>({ institutionId: props.institutionId })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>商品の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditHomeProductFormProps = {
    homeProduct: HomeProduct
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditHomeProductForm = (props: EditHomeProductFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutHomeProductApi()
    const form = useForm<HomeProductForm>({ ...props.homeProduct, productItemIds: props.homeProduct.productItems?.map((pi) => pi.id!) })

    useEffectSkipFirst(() => {
        form.set({ ...props.homeProduct, productItemIds: props.homeProduct.productItems?.map((pi) => pi.id!) })
    }, [props.homeProduct])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>商品の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
